import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export const HOUSE_ACCOUNTS_SETTING_KEY = 'house_accounts'

export interface HouseAccountsSetting {
  staff_allowances: boolean
  customer_tabs: boolean
}

export interface HouseAccountsGate {
  /** Staff meal allowances are switched on (Settings → Staff meals & tabs). */
  staff: boolean
  /** Customer tabs are switched on — new tab charges allowed. */
  tabs: boolean
  /**
   * Tab payment button, tab reports and tab lines: tabs are on, OR they were
   * switched off while a guest still owes, so the money can still be collected.
   */
  tabsVisible: boolean
  /** staff || tabsVisible. */
  any: boolean
  isLoading: boolean
}

/** Reads the stored `house_accounts` value; anything malformed reads as both off. */
export function houseAccountsSettingFrom(raw: unknown): HouseAccountsSetting {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const r = raw as Record<string, unknown>
    return { staff_allowances: r.staff_allowances === true, customer_tabs: r.customer_tabs === true }
  }
  return { staff_allowances: false, customer_tabs: false }
}

/** Pure gate derivation from the GET /settings map. */
export function resolveHouseAccountsGate(
  all: Record<string, unknown> | undefined,
): Omit<HouseAccountsGate, 'isLoading'> {
  const s = houseAccountsSettingFrom(all?.[HOUSE_ACCOUNTS_SETTING_KEY])
  const staff = s.staff_allowances
  const tabs = s.customer_tabs
  const tabsVisible = tabs || all?.house_accounts_tabs_owing === true
  return { staff, tabs, tabsVisible, any: staff || tabsVisible }
}

/**
 * useHouseAccountsEnabled reads the admin's `house_accounts` switch (both off
 * by default) and the server-injected `house_accounts_tabs_owing` flag from
 * GET /settings (F-MM-N30). Shares the `['settings','all']` cache.
 */
export function useHouseAccountsEnabled(): HouseAccountsGate {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
  })
  return { ...resolveHouseAccountsGate(data?.data as Record<string, unknown> | undefined), isLoading }
}
